/**
 * GlassToggle — the app's segmented toggle pill: one carved-well capsule with
 * a sliding accent chip under the active option (the `.mode-pill*` CSS in
 * apps/web/src/index.css). Use it wherever a small set of mutually-exclusive
 * lenses/modes would otherwise become a row of loose chips.
 *
 * The chip is JS-measured off the active button (offsetLeft/offsetWidth)
 * rather than CSS-guessed, so variable-width labels — and option sets that
 * change at runtime — track correctly. The slide transition is enabled only
 * after the first paint has positioned it, so it doesn't fly in from 0.
 *
 * Lives in core-ui (not @papan/collab, whose ModeToggle adds overflow-into-a-
 * chevron on top of the same CSS) because collab depends on
 * @papan/share-links/ui — importing it there would close a cycle.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

export interface GlassToggleOption<T extends string> {
  value: T
  label: ReactNode
  icon?: ReactNode
  /** Overrides the sliding chip's colour for this option (default: indigo). */
  accent?: string
  /** Active label colour — set it when `accent` is light enough to hurt contrast. */
  activeText?: string
  title?: string
  testId?: string
}

export function GlassToggle<T extends string>({
  value,
  onChange,
  options,
  className = '',
  tablist,
  testId,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<GlassToggleOption<T>>
  className?: string
  /**
   * Opt into tab semantics (container `tablist`, buttons `tab`/`aria-selected`)
   * when the pill switches which *set of content* is rendered below it. Left
   * off, it's a plain button group with `aria-pressed` — right for a pill that
   * reconfigures a form in place.
   */
  tablist?: boolean
  testId?: string
}) {
  const pillRef = useRef<HTMLDivElement>(null)
  const [slider, setSlider] = useState<{ left: number; width: number } | null>(null)
  const [ready, setReady] = useState(false)

  const measure = useCallback(() => {
    const pill = pillRef.current
    if (!pill) return
    const btn = pill.querySelector<HTMLElement>(`[data-mode="${CSS.escape(value)}"]`)
    if (!btn) { setSlider(null); return }
    setSlider({ left: btn.offsetLeft, width: btn.offsetWidth })
  }, [value])

  useLayoutEffect(() => { measure() }, [measure])
  useEffect(() => {
    const t = requestAnimationFrame(() => setReady(true))
    const pill = pillRef.current
    // Labels reflow (i18n, container resize) without `value` changing — remeasure.
    const ro = new ResizeObserver(() => measure())
    if (pill) ro.observe(pill)
    return () => { cancelAnimationFrame(t); ro.disconnect() }
  }, [measure])

  const activeOpt = options.find((o) => o.value === value)

  return (
    <div
      ref={pillRef}
      className={`mode-pill w-fit ${className}`}
      role={tablist ? 'tablist' : 'group'}
      data-testid={testId}
    >
      {slider && (
        <span
          className="mode-pill-slider"
          data-ready={ready ? '' : undefined}
          style={{
            left: slider.left,
            width: slider.width,
            ...(activeOpt?.accent ? { background: activeOpt.accent, boxShadow: `0 1px 6px color-mix(in srgb, ${activeOpt.accent} 40%, transparent)` } : {}),
          }}
        />
      )}
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            data-mode={o.value}
            onClick={() => onChange(o.value)}
            role={tablist ? 'tab' : undefined}
            aria-selected={tablist ? active : undefined}
            aria-pressed={tablist ? undefined : active}
            title={o.title}
            data-testid={o.testId}
            className={`mode-pill-btn whitespace-nowrap ${active ? 'active' : ''}`}
            style={active && o.activeText ? { color: o.activeText } : undefined}
          >
            {o.icon}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
